/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Box, Fade, Stack, Typography, useMediaQuery, useTheme } from '@mui/material';
import { useSelector } from 'react-redux';
import { Button } from '../../../components/Button';
import {
  BoldWrapper,
  OrderLocationButton,
  PadButton,
  PadContent,
  PadIcon,
  WrapperA,
} from '../../../components/Pad/Pad';
import { colors } from '@farmlands/mui-theme';
import { selectCurrentFarmerAccount } from '../../azure-b2c/storage';
import CartSlice, {
  checkoutV2,
  selectCurrentCart,
  selectOrderStatusById,
} from '../storage/cart.slice';
import { formatPrice } from '../../../util/helpers';
import { Loader, LoadingBox } from '../../../components/Loader';
import { useAppDispatch } from '../../../store/rootStore';
import { URLS } from '../../../util/consts';
import { CartDeliveryMode, CartObject } from '../storage/cart.types';
import { handleErrors } from '../utils/handleErrors';
import { TestIds } from '../../../testIds';
import Modal from '../../../components/Modal';
import { OrderSummary } from '../../../components/OrderSummary';
import Checkbox from '../../../components/Checkbox';
import { removeGst } from '../../../util/removeGst';
import { ContactComponents, useContactInfo } from '../../drawdown/v1/forms/ContactInfo';
import { StockChangesAlert } from './PickupStoreSwitchAlertModal';
import { checkStockChanges } from '../utils/checkStockChanges';
import { hasUrgentDeliveryUnavailable } from '../utils/getUrgentDeliveryUnavailable';
import { addGST } from '../../../util/addGst';
import { calculateEditedPriceDiscount } from '../../../util/calculateEditedPriceDiscount';
import {
  FulfilmentFAQsText,
  TermsAndConditionsText,
} from '../../../components/layout/TermsAndConditionsText';
import MaterialSymbol from '../../../components/MaterialSymbol';
import { useQuoteModal } from '../../quotes/components/QuoteModal/useQuoteModal';
import {
  Container,
  SubmitButton,
  ReferenceNoteWrapper,
  Textarea,
  ButtonBox,
  GapContainer,
  InlineFlex,
} from './CartSummaryComponents';
import { CartMessage } from '../utils/cartMessages';

interface CartSummaryProps {
  handleOpenPickUpStoreModal: () => void;
}

export const CartSummary = (props: CartSummaryProps) => {
  const dispatch = useAppDispatch();
  const currentAccount = useSelector(selectCurrentFarmerAccount);
  const isTfo = currentAccount?.hasTfoAccess || false;
  const currentCart = useSelector(selectCurrentCart);
  const orderStatus = useSelector(selectOrderStatusById(currentCart?.offlineCartId));
  const theme = useTheme();
  const isLg = useMediaQuery(theme.breakpoints.up('lg'));
  const [isErrorOnPickupStore, setIsErrorOnPickupStore] = React.useState<boolean>(false);
  const [isNotesOpen, setIsNotesOpen] = useState(false);
  const [isInternalNotesOpen, setIsInternalNotesOpen] = useState(false);
  const elRef = useRef<HTMLElement>(null);
  const notesTextareaRef = useRef<HTMLTextAreaElement>(null);
  const internalNotesTextareaRef = useRef<HTMLTextAreaElement>(null);
  const [buttonBoxHeight, setButtonBoxHeight] = useState(80);
  const [isStockAlertModalOpen, setIsStockAlertModalOpen] = useState(false);

  const scrollToBottom = () => {
    if (!isLg) {
      const mc = document.getElementById('main-scroll-container');
      if (mc) {
        const scrollDiff = mc.clientHeight + mc.scrollTop;
        if (scrollDiff < mc.scrollHeight - 20) {
          mc.scrollTo({
            behavior: 'smooth',
            top: mc.scrollHeight + 100,
            left: 0,
          });
        }
      }
    }
  };

  const error = useMemo(() => {
    if (orderStatus?.error && orderStatus?.status === 'rejected' && currentAccount) {
      scrollToBottom();
      const _error = handleErrors(currentAccount.accountNumber, orderStatus?.error, dispatch);
      return _error;
    }
  }, [orderStatus, currentAccount]);

  const loading = useMemo(() => {
    return orderStatus?.status === 'pending' || currentCart?.isQuantityChanged === true;
  }, [orderStatus, currentCart?.isQuantityChanged]);

  useLayoutEffect(() => {
    if (!elRef.current?.offsetHeight) {
      return;
    }
    const resizeObserver = new ResizeObserver(() => {
      if (elRef?.current && elRef?.current.offsetHeight !== buttonBoxHeight) {
        setButtonBoxHeight(elRef?.current.offsetHeight);
      }
    });
    resizeObserver.observe(elRef.current);
    return function cleanup() {
      resizeObserver.disconnect();
    };
  }, [elRef.current, error]);

  useEffect(() => {
    if (currentCart?.notes) setIsNotesOpen(true);
    if (currentCart?.internalNotes) setIsInternalNotesOpen(true);
    setIsNotesOpen(false);
    setIsInternalNotesOpen(false);
  }, [currentCart?.notes, currentCart?.internalNotes]);

  const handleValidation = () => {
    if (
      currentCart?.pickupStore === undefined &&
      currentCart?.cartDeliveryMode === CartDeliveryMode.PICKUP
    ) {
      setIsErrorOnPickupStore(true);
      scrollToBottom();
      return false;
    }
    setIsErrorOnPickupStore(false);
    return true;
  };

  const handleContinue = async () => {
    if (!currentAccount || !currentCart) return;

    const checkStock = await checkStockChanges(currentCart, dispatch);
    if (checkStock?.isChanged) {
      dispatch(
        CartSlice.actions.setStockAvailability({
          accountNumber: currentAccount.accountNumber,
          productWithStockAvailabilities: checkStock.mergedItems,
        }),
      );
      setIsStockAlertModalOpen(true);
      return;
    }
    if (!handleValidation()) return;
    setIsModalOpen(true);
  };

  const isShowingDeliveryCostMsg = React.useMemo(() => {
    return (
      currentAccount &&
      currentCart?.cartDeliveryMode === CartDeliveryMode.DELIVERY &&
      currentCart?.deliveryDetails &&
      removeGst(currentCart?.totalAmountIncGst) < 500
    );
  }, [currentAccount, currentCart]);

  const handleSubmitOrder = (accountNumber: string, cart: CartObject) => {
    scrollToBottom();
    if (!handleValidation()) return;
    dispatch(
      checkoutV2({
        accountNumber,
        cart: {
          ...cart,
          email: email,
          phone: phone,
          customerReference: reference,
          customerName: name,
        },
      }),
    );
  };

  const renderEditPickUpStoreButton = React.useMemo(() => {
    if (
      (currentCart?.pickupStore === undefined &&
        currentCart?.cartDeliveryMode === CartDeliveryMode.PICKUP) ||
      (currentCart?.deliveryDetails === undefined &&
        currentCart?.cartDeliveryMode === CartDeliveryMode.DELIVERY)
    ) {
      return (
        <PadButton
          shade='light'
          sx={{ justifyContent: 'flex-start', p: 1 }}
          onClick={props.handleOpenPickUpStoreModal}
          data-testid={TestIds.feat.cart.summary.pickUpButton}
          aria-invalid={isErrorOnPickupStore}
          aria-errormessage={isErrorOnPickupStore ? 'pickup-error' : undefined}
          errorMessage={isErrorOnPickupStore ? 'Pickup store required' : undefined}
        >
          <WrapperA>
            <BoldWrapper>
              <PadIcon className='material-symbols-outlined'>add</PadIcon>
              <PadContent>Select pickup store or delivery</PadContent>
            </BoldWrapper>
          </WrapperA>
        </PadButton>
      );
    }

    if (currentCart?.cartDeliveryMode === CartDeliveryMode.PICKUP && currentCart?.pickupStore)
      return (
        <OrderLocationButton
          onClick={props.handleOpenPickUpStoreModal}
          buttonTestId={TestIds.feat.cart.summary.pickUpButton}
          icon='location_on'
          title='Pickup'
          subtitle={currentCart.pickupStore.orgUnitName}
          isEditEnabled={true}
        />
      );
    if (
      currentCart?.cartDeliveryMode === CartDeliveryMode.DELIVERY &&
      currentCart?.deliveryDetails
    ) {
      return (
        <OrderLocationButton
          onClick={props.handleOpenPickUpStoreModal}
          buttonTestId={TestIds.feat.cart.summary.pickUpButton}
          icon='local_shipping'
          title='Delivery'
          subtitle={currentCart.deliveryDetails.address}
          isEditEnabled={true}
        />
      );
    }
  }, [currentCart, isErrorOnPickupStore]);

  const renderDeliveryCostNotice = () => {
    if (isShowingDeliveryCostMsg) {
      return (
        <Box
          component='span'
          display='flex'
          flex={'1 0 auto'}
          borderLeft={`5px solid ${colors.primary500}`}
          paddingLeft={'16px'}
          sx={{ overflowWrap: 'anywhere', flexDirection: 'column' }}
        >
          <Typography
            variant='body2'
            fontWeight={600}
            data-testid={TestIds.feat.cart.summary.deliveryCostNotice}
          >
            {CartMessage.deliveryCostExcluded}
            <br />
            <Typography variant='body2' component='span'>
              {CartMessage.deliveryCost}
            </Typography>
          </Typography>
        </Box>
      );
    }
  };

  const scrollToTop = () => {
    setTimeout(() => {
      const mc = document.getElementById('main-scroll-container');
      mc?.scrollTo({
        behavior: 'smooth',
        top: mc.scrollHeight + 500,
        left: 0,
      });
    }, 100);
  };

  const handleNotesFocus = (textareaRef: React.RefObject<HTMLTextAreaElement>) => {
    {
      setTimeout(() => {
        textareaRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 300);
    }
  };

  const handleAddNotes = useCallback(() => {
    setIsNotesOpen(true);
    scrollToTop();
  }, [setIsNotesOpen]);

  const handleAddInternalNotes = useCallback(() => {
    setIsInternalNotesOpen(true);
    scrollToTop();
  }, [setIsInternalNotesOpen]);

  const [isModalOpen, setIsModalOpen] = React.useState(false);
  const quoteProvider = useQuoteModal();

  const { email, phone, name, reference, validateContactInfo, ContactInfoComponent } =
    useContactInfo({
      components: [
        ContactComponents.NAME,
        ContactComponents.EMAIL,
        ContactComponents.PHONE,
        ContactComponents.REFERENCE,
      ],
      isTfo,
    });

  const onSubmit = () => {
    if (validateContactInfo() && currentAccount && currentCart) {
      handleSubmitOrder(currentAccount.accountNumber, currentCart);
      setIsModalOpen(false);
    }
  };

  const getAlertModal = useMemo(() => {
    if (isStockAlertModalOpen)
      return (
        <StockChangesAlert
          open={isStockAlertModalOpen}
          handleClose={() => setIsStockAlertModalOpen(false)}
        />
      );

    const message = `Please provide contact details${isTfo ? ' for the customer' : ''}, in case a Farmlands representative needs to contact ${isTfo ? 'them' : 'you'} regarding this order.`;

    const shareholderDetails = isTfo ? (
      <Stack direction='row' spacing={1} mb={3}>
        <Box bgcolor={colors.primary500} width='4px' alignSelf='stretch'></Box>
        <Stack direction='column'>
          <Typography variant='body1' color={colors.grey800} fontWeight='700'>
            {currentAccount?.accountName}
          </Typography>
          <Typography variant='body2' color={colors.grey700}>
            {currentAccount?.accountNumber}
          </Typography>
        </Stack>
      </Stack>
    ) : null;

    return (
      <Modal
        title={isTfo ? 'Confirm customer details' : 'Confirm your details'}
        open={isModalOpen}
        data-testid={TestIds.feat.checkout.contactModal}
        handleClose={() => {
          setIsModalOpen(false);
        }}
        header={<Typography variant='body1'>{message}</Typography>}
        footer={
          <SubmitButton
            variant='contained'
            type='submit'
            onClick={() => onSubmit()}
            data-testid={TestIds.feat.checkout.contactModalSubmit}
          >
            Submit order
          </SubmitButton>
        }
      >
        {shareholderDetails}
        {ContactInfoComponent}
      </Modal>
    );
  }, [isModalOpen, isStockAlertModalOpen, onSubmit]);

  const renderAddNotes = useMemo(() => {
    const showButton = !isNotesOpen && !currentCart?.notes;

    if (showButton)
      return (
        <PadButton
          shade='light'
          sx={{
            justifyContent: 'flex-start',
            p: 1,
          }}
          onClick={handleAddNotes}
          data-testid={TestIds.feat.cart.summary.addNotesButton}
        >
          <WrapperA>
            <BoldWrapper>
              <PadIcon className='material-symbols-outlined'>add</PadIcon>
              <PadContent>Add customer notes</PadContent>
            </BoldWrapper>
          </WrapperA>
        </PadButton>
      );
    return (
      <Fade in={!showButton}>
        <ReferenceNoteWrapper>
          <Textarea
            aria-label='Customer facing note for order'
            label='Customer facing notes'
            value={currentCart?.notes ?? ''}
            onChange={(event) => {
              if (event.target.value.length <= 200 && currentAccount)
                dispatch(
                  CartSlice.actions.saveNotes({
                    accountNumber: currentAccount.accountNumber,
                    notes: event.target.value,
                  }),
                );
            }}
            InputProps={{
              ref: notesTextareaRef,
              onFocus: () => handleNotesFocus(notesTextareaRef),
            }}
            inputProps={{
              maxLength: 200,
            }}
            autoFocus={!currentCart?.notes}
            multiline
            minRows={4}
            variant='outlined'
            helperText={`${currentCart?.notes ? currentCart?.notes?.length : 0}/200 characters`}
            testId={TestIds.feat.cart.summary.notesField}
          />
        </ReferenceNoteWrapper>
      </Fade>
    );
  }, [currentCart?.notes, isNotesOpen]);

  const renderAddInternalNotes = useMemo(() => {
    if (!currentAccount?.hasTfoAccess) return;

    const showButton = !isInternalNotesOpen && !currentCart?.internalNotes;

    if (showButton)
      return (
        <PadButton
          shade='light'
          sx={{
            justifyContent: 'flex-start',
            p: 1,
          }}
          onClick={handleAddInternalNotes}
          data-testid={TestIds.feat.cart.summary.addInternalNotesButton}
        >
          <WrapperA>
            <BoldWrapper>
              <PadIcon className='material-symbols-outlined'>add</PadIcon>
              <PadContent>Add internal notes</PadContent>
            </BoldWrapper>
          </WrapperA>
        </PadButton>
      );
    return (
      <Fade in={!showButton}>
        <ReferenceNoteWrapper>
          <Textarea
            aria-label='Internal note for order'
            label='Internal only notes'
            value={currentCart?.internalNotes ?? ''}
            onChange={(event) => {
              if (event.target.value.length <= 200 && currentAccount)
                dispatch(
                  CartSlice.actions.saveInternalNotes({
                    accountNumber: currentAccount.accountNumber,
                    internalNotes: event.target.value,
                  }),
                );
            }}
            InputProps={{
              ref: internalNotesTextareaRef,
              onFocus: () => handleNotesFocus(internalNotesTextareaRef),
            }}
            inputProps={{ maxLength: 200 }}
            autoFocus={!currentCart?.internalNotes}
            multiline
            minRows={4}
            variant='outlined'
            helperText={`${currentCart?.internalNotes ? currentCart?.internalNotes?.length : 0}/200 characters`}
            testId={TestIds.feat.cart.summary.internalNotesField}
          />
        </ReferenceNoteWrapper>
      </Fade>
    );
  }, [currentCart?.internalNotes, isInternalNotesOpen]);

  const submitOrLoading = useMemo(() => {
    if (currentCart && currentAccount) {
      if (loading) {
        return (
          <LoadingBox>
            <Loader title='Syncing your cart' type='inline-flex' />
          </LoadingBox>
        );
      }

      return (
        <ButtonBox ref={elRef}>
          {error && (
            <Typography variant='body1' color={colors.error700}>
              {error}
            </Typography>
          )}
          <Button
            variant='contained'
            sx={{ width: '100%' }}
            onClick={handleContinue}
            data-testid={TestIds.feat.cart.summary.continueButton}
          >
            Continue to checkout
          </Button>

          {quoteProvider.isCartQuotable(currentCart) && (
            <Button
              variant='outlined'
              sx={{ width: '100%' }}
              onClick={() => quoteProvider.quoteModalFromCart(currentCart, isTfo)}
              data-testid={TestIds.feat.cart.summary.requestCartQuote}
            >
              {isTfo ? 'Send a quote' : 'Request a quote'}
            </Button>
          )}
        </ButtonBox>
      );
    }
  }, [
    currentCart,
    loading,
    currentAccount,
    error,
    elRef,
    orderStatus,
    quoteProvider.isCartQuotable,
  ]);

  const urgentDeliveryUnavailable: boolean = useMemo(() => {
    if (!currentCart || !currentAccount) return false;
    return hasUrgentDeliveryUnavailable({
      cart: currentCart,
      items: currentCart?.items,
      account: currentAccount,
    });
  }, [currentCart, currentAccount]);

  const calculateDiscount = () => {
    const allDiscounts = currentCart?.discountIncGst ?? 0;
    const editedItemDiscounts =
      currentCart?.items
        .filter((item) => {
          return (
            item?.editedPriceExcGst &&
            item.editedPriceExcGst > 0 &&
            item?.originalPriceExcGst &&
            item.originalPriceExcGst > 0
          );
        })
        .reduce((acc, item) => acc + calculateEditedPriceDiscount(item), 0) ?? 0;
    return formatPrice(allDiscounts + addGST(editedItemDiscounts));
  };

  return (
    <Container buttonBoxHeight={buttonBoxHeight}>
      <GapContainer gap='1rem'>
        <OrderSummary
          title='Order summary'
          accountName={currentAccount?.accountName}
          accountNumber={currentAccount?.accountNumber}
          total={formatPrice(currentCart?.totalPrice ?? 0)}
          gst={formatPrice(currentCart?.gstAmount ?? 0)}
          delivery={!isShowingDeliveryCostMsg ? 'Free' : undefined}
          discount={calculateDiscount()}
          showDivider
        />
        <InlineFlex>
          <Box component='span'>
            <Typography variant='body2' data-testid={TestIds.feat.cart.summary.orderChargedMessage}>
              {CartMessage.chargedToAccount}
            </Typography>
          </Box>
        </InlineFlex>
        <a href={URLS.termsAndConditions} target='_blank' rel='noreferrer'>
          <Stack direction='row' alignItems='center' spacing={1}>
            <MaterialSymbol sx={{ color: colors.primary500 }}>Description</MaterialSymbol>
            <TermsAndConditionsText />
          </Stack>
        </a>
        <a href={URLS.fulfilmentFAQs} target='_blank' rel='noreferrer'>
          <Stack direction='row' alignItems='center' spacing={1}>
            <MaterialSymbol sx={{ color: colors.primary500 }}>Description</MaterialSymbol>
            <FulfilmentFAQsText />
          </Stack>
        </a>
      </GapContainer>
      <GapContainer gap='1rem' data-testid={TestIds.feat.cart.summary.errorMessage}>
        <GapContainer>{renderDeliveryCostNotice()}</GapContainer>
        <GapContainer>{renderEditPickUpStoreButton}</GapContainer>
        <GapContainer>{renderAddNotes}</GapContainer>
        <GapContainer>{renderAddInternalNotes}</GapContainer>
        {currentAccount?.hasTfoAccess && <GapContainer />}

        {currentAccount && currentCart?.cartDeliveryMode === CartDeliveryMode.DELIVERY && (
          <Checkbox
            checked={currentCart?.deliveryDetails?.urgent}
            label={
              <Typography variant='body2' fontWeight={700} sx={{ paddingLeft: '12px' }}>
                I want this delivered urgently
                <br />
                <Typography variant='body2' component='span'>
                  Additional delivery fees may apply
                </Typography>
              </Typography>
            }
            onChange={() =>
              dispatch(
                CartSlice.actions.setUrgent({
                  accountNumber: currentAccount.accountNumber,
                  urgent: !currentCart?.deliveryDetails?.urgent,
                }),
              )
            }
          />
        )}
        {urgentDeliveryUnavailable && (
          <Typography
            variant='body2'
            color={colors.secondary800}
            pl={'44px'}
            mt={-1}
            data-testid={TestIds.feat.cart.summary.urgentDeliveryWarning}
          >
            Some items are not available urgently. Please review the items in your cart.
          </Typography>
        )}

        {submitOrLoading}
      </GapContainer>
      {getAlertModal}
    </Container>
  );
};

export default CartSummary;
